/**
 * Basecamp API Client
 * Wraps all Basecamp API endpoints through the backend proxy
 *
 * Authentication is handled via OAuth2 flow on the backend.
 * If not authenticated, the API will return a 401 with auth_url.
 */

import { toast } from 'sonner';
import { fetchWithAuth, API_BASE_URL, isOfflineOrUnreachableFetchError } from '@/lib/api';
import { normalizeAssignmentRecordingFields } from '@/lib/basecamp-assignment-meta';
import { todayInLosAngeles } from '@/lib/basecamp-calendar';
import { buildBasecampWebUrl } from '@/lib/basecamp-external-url';
import type { BasecampAssignment } from '@/types/basecamp';

export type { BasecampAssignment };

/**
 * Fallback Basecamp OAuth entry page on the API host (HTML with Launchpad link).
 * Prefer `auth_url` from `/api/auth-status` or 401 responses (includes OAuth state for DB token save).
 */
export function getBasecampAuthUrl(): string {
  return `${API_BASE_URL}/auth/basecamp`;
}

const BASECAMP_AUTH_POPUP = 'width=600,height=700';

/** Open Basecamp OAuth in a popup; prefer auth_url from /api/auth-status (includes state for DB save). */
export function openBasecampAuthWindow(authUrl?: string): void {
  window.open(authUrl || getBasecampAuthUrl(), 'basecamp-auth', BASECAMP_AUTH_POPUP);
}

/** Parse bucket + todo ids from a Basecamp 3 todo URL. */
export function parseBasecampTodoIdsFromUrl(todoUrl: string): { projectId: string; todoId: string } | null {
  const match = String(todoUrl || '').match(/\/buckets\/(\d+)\/todos\/(\d+)/i);
  if (!match) return null;
  return { projectId: match[1], todoId: match[2] };
}

const REAUTH_TOAST_ID = 'basecamp-reauth-needed';
const DISCONNECTED_TOAST_ID = 'basecamp-disconnected';

/** One disconnect toast. 401s reuse this id so they do not stack a second notice. */
export function showBasecampDisconnectedToast(authUrl?: string): void {
  toast.dismiss(REAUTH_TOAST_ID);
  toast.warning('Basecamp is disconnected', {
    id: DISCONNECTED_TOAST_ID,
    description: 'This workspace is not signed in. Reconnect to use live Basecamp.',
    duration: Infinity,
    closeButton: true,
    action: {
      label: 'Reconnect',
      onClick: () => openBasecampAuthWindow(authUrl),
    },
  });
}

export function dismissBasecampDisconnectedToast(): void {
  toast.dismiss(DISCONNECTED_TOAST_ID);
}

/**
 * Handle 401 from Basecamp API: show toast with reconnect action, invalidate auth status.
 * Call when a Basecamp API response returns 401.
 */
export function handleBasecamp401(_response: Response, data?: { auth_url?: string; error?: string }): never {
  const authUrl = data?.auth_url || getBasecampAuthUrl();
  window.dispatchEvent(new CustomEvent('basecamp-auth-invalidated'));
  showBasecampDisconnectedToast(authUrl);
  throw Object.assign(new Error(data?.error || 'Basecamp needs re-authorization'), {
    status: 401,
    auth_url: authUrl,
  });
}

/**
 * Fetch a Basecamp API endpoint. On 401, parses body, shows re-auth toast, and throws.
 * Use this instead of fetchWithAuth for Basecamp endpoints so 401 is handled consistently.
 */
export async function fetchBasecampApi(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetchWithAuth(url, init);
  if (res.status === 401) {
    const data = await res.json().catch(() => ({}));
    handleBasecamp401(res, data);
  }
  return res;
}

/**
 * Check if error is an authentication error.
 * For 401: shows re-auth toast, invalidates auth status, then throws.
 */
function handleAuthError(error: any): never {
  if (error.status === 401 || error.statusCode === 401) {
    handleBasecamp401({} as Response, {
      auth_url: error.auth_url || getBasecampAuthUrl(),
      error: error.error || error.message,
    });
  }
  if (error.status === 503 || error.error === 'database_unavailable') {
    const prodHint =
      'The hosted API could not reach the database (often pooler saturation or a short outage). Wait a minute and retry, or check Railway / Supabase status.';
    const devHint =
      'PostgreSQL did not accept a connection. Start the DB (e.g. npm start / Docker) and retry.';
    const raw = typeof error.message === 'string' ? error.message.trim() : '';
    const looksLikeLocalDevCopy = /npm start|Docker|PGHOST|localhost:3000|5432/i.test(raw);
    const description = import.meta.env.PROD
      ? !raw || looksLikeLocalDevCopy
        ? prodHint
        : raw
      : raw || devHint;
    toast.error('Database unavailable', {
      description,
      duration: 8000,
    });
  }
  throw error;
}

export interface BasecampProject {
  id: string;
  name: string;
  description?: string;
  url: string;
  dock: any[];
  lastSync?: Date;
  listsCachedAt?: string | null;
  listsFresh?: boolean;
  cacheDepth?: 'ok' | 'partial' | 'none';
}

export interface BasecampTodo {
  id: string;
  title: string;
  description?: string;
  completed: boolean;
  due_on?: string;
  assigned_to?: {
    id: string;
    name: string;
    avatar_url?: string;
  };
  url: string;
  comments_count: number;
  parent?: {
    id: string;
    title: string;
  };
}

export interface BasecampComment {
  id: string;
  content: string;
  created_at: string;
  author: {
    id: string;
    name: string;
    avatar_url?: string;
  };
}

export interface BasecampActivity {
  id: string;
  type: 'todo' | 'message' | 'document';
  title: string;
  url: string;
  created_at: string;
  creator: {
    name: string;
  };
}

export interface BasecampAuthStatus {
  authenticated: boolean;
  auth_url?: string;
  /** True when /api/auth-status failed with 5xx (e.g. DB unreachable). Distinct from "Basecamp not connected". */
  apiUnavailable?: boolean;
}

/** Related Basecamp todo (keyword match in connections panel) */
export interface RelatedTodo {
  id: string;
  projectId?: string | null;
  title: string;
  status: string;
  dueOn: string | null;
  projectName: string | null;
  appUrl: string | null;
}

/** Append ?organizationId= or &organizationId= for workspace-scoped Basecamp API calls. */
export function withOrganizationId(url: string, organizationId?: string): string {
  if (!organizationId) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}organizationId=${encodeURIComponent(organizationId)}`;
}

/** One ordered ingest routing rule. The Settings UI only ever writes `list_id`. */
export interface BasecampIngestFolderRule {
  match: 'list_id' | 'list_contains'
  /** Cached Basecamp to-do list id (present for `list_id` rules) */
  listId?: string
  /** Human label for `list_id`, or the matched substring for seeded `list_contains` */
  value?: string
  folder: string
}

export interface BasecampIngestOverride {
  projectId: string
  projectName?: string | null
  folder: string
  rules: BasecampIngestFolderRule[]
}

export interface BasecampIngestCatalogList {
  id: string
  title: string
  parentTitle?: string | null
  isNested?: boolean
}

export interface BasecampIngestCatalogProject {
  projectId: string
  name: string
  status?: string | null
  folder: string
  rules: BasecampIngestFolderRule[]
  lists: BasecampIngestCatalogList[]
}

export interface BasecampIngestConfig {
  success?: boolean
  organization_id: string
  organization_slug?: string | null
  default_folder: string
  /** True when nothing is stored yet and `default_folder` is only a suggestion */
  default_folder_is_suggested: boolean
  suggested_default_folder: string
  overrides: BasecampIngestOverride[]
  catalog: BasecampIngestCatalogProject[]
  can_edit?: boolean
}

export interface BasecampIngestConfigUpdate {
  default_folder?: string
  overrides?: Array<{
    projectId: string
    folder?: string | null
    rules?: BasecampIngestFolderRule[]
  }>
}

/**
 * Basecamp API client
 */
export const basecampApi = {
  /**
   * Workspace ingest folder configuration (Settings > Extensions > Basecamp > Project folders)
   */
  getIngestConfig: async (organizationId?: string): Promise<BasecampIngestConfig> => {
    const response = await fetchWithAuth(
      withOrganizationId('/api/basecamp/ingest-config', organizationId),
      {
        credentials: 'include',
        headers: organizationId ? { 'x-organization-id': organizationId } : undefined,
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw Object.assign(
        new Error(errorData?.error || 'Failed to load Basecamp project folders'),
        { status: response.status }
      );
    }
    return await response.json();
  },

  /** Save the default ingest folder and/or per-project overrides (admin/owner only) */
  updateIngestConfig: async (
    update: BasecampIngestConfigUpdate,
    organizationId?: string
  ): Promise<BasecampIngestConfig & { updated?: unknown[]; skipped?: unknown[] }> => {
    const response = await fetchWithAuth(
      withOrganizationId('/api/basecamp/ingest-config', organizationId),
      {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(organizationId ? { 'x-organization-id': organizationId } : {}),
        },
        body: JSON.stringify(update),
      }
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw Object.assign(
        new Error(errorData?.error || 'Failed to save Basecamp project folders'),
        { status: response.status }
      );
    }
    return await response.json();
  },

  /**
   * Check authentication status
   */
  getAuthStatus: async (organizationId?: string): Promise<BasecampAuthStatus> => {
    try {
      const response = await fetchWithAuth(withOrganizationId(`/api/auth-status`, organizationId), {
        credentials: 'include', // Include cookies for auth token
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const apiUnavailable =
          response.status >= 502 ||
          data.error === 'database_unavailable';
        return {
          authenticated: false,
          auth_url: data.auth_url || getBasecampAuthUrl(),
          apiUnavailable,
        };
      }

      return { ...data, apiUnavailable: false };
    } catch (error) {
      if (!isOfflineOrUnreachableFetchError(error)) {
        console.error('Auth status check failed:', error);
      }
      return {
        authenticated: false,
        auth_url: getBasecampAuthUrl(),
        apiUnavailable: true,
      };
    }
  },

  /**
   * Fetch all Basecamp projects
   */
  getProjects: async (organizationId?: string, forceRefresh = false): Promise<BasecampProject[]> => {
    try {
      const qs = forceRefresh ? '?refresh=true' : '';
      const response = await fetchWithAuth(withOrganizationId(`/api/basecamp/projects${qs}`, organizationId), {
        credentials: 'include', // Include cookies for auth token
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      const data = await response.json();
      return data.projects || data; // Handle both wrapped and unwrapped responses
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      throw error;
    }
  },

  /**
   * Fetch todos for a specific project
   */
  getTodos: async (projectId: string, forceRefresh: boolean = false): Promise<BasecampTodo[]> => {
    try {
      const params = forceRefresh ? '?refresh=true' : '';
      const response = await fetchWithAuth(`/api/todos/${projectId}${params}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`[Basecamp API] Error fetching todos for project ${projectId}:`, response.status, errorData);
        handleAuthError({ status: response.status, ...errorData });
      }
      const data = await response.json();

      // Handle various response formats
      if (data.todoLists) {
        // Flatten todoLists into a single array of todos
        const allTodos: BasecampTodo[] = [];
        for (const list of data.todoLists) {
          if (list.todos && Array.isArray(list.todos)) {
            allTodos.push(...list.todos);
          }
        }
        return allTodos;
      }

      if (data.todos && Array.isArray(data.todos)) {
        return data.todos;
      }

      if (Array.isArray(data)) {
        return data;
      }

      // If no todos found, return empty array
      return [];
    } catch (error) {
      console.error(`[Basecamp API] Error fetching todos for project ${projectId}:`, error);
      throw error;
    }
  },

  /**
   * Fetch todo lists for a specific project (with todos grouped by list)
   */
  getTodoLists: async (projectId: string, forceRefresh: boolean = false): Promise<{
    todoLists: Array<{
      id: string;
      title: string;
      todos: BasecampTodo[];
      parent_id?: string | null;
      is_nested?: boolean;
    }>;
    timestamp?: string;
    account?: {
      id: string;
      name: string;
    };
    project_id?: string;
    message?: string;
    _cached?: boolean;
    _cached_at?: string | null;
    _fetched_at?: string;
    _stale?: boolean;
  }> => {
    try {
      const params = forceRefresh ? '?refresh=true' : '';
      const url = `/api/todos/${projectId}${params}`;
      console.log(`[Basecamp API] Fetching todo lists from: ${url}`);

      const response = await fetchWithAuth(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`[Basecamp API] Error fetching todo lists for project ${projectId}:`, {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        handleAuthError({ status: response.status, ...errorData });
      }

      const data = await response.json();
      console.log(`[Basecamp API] Raw response for todo lists:`, {
        hasTodoLists: !!data.todoLists,
        todoListsType: Array.isArray(data.todoLists) ? 'array' : typeof data.todoLists,
        todoListsLength: Array.isArray(data.todoLists) ? data.todoLists.length : 'N/A',
        message: data.message,
        fullResponse: data
      });

      // Return the structured todoLists format
      if (data.todoLists && Array.isArray(data.todoLists)) {
        const mapped = data.todoLists.map((list: any) => ({
          id: list.id?.toString() || '',
          title: list.title || 'Untitled List',
          todos: Array.isArray(list.todos) ? list.todos : [],
          // Hierarchy fields for nested todolists/sections
          parent_id: list.parent_id || null,
          parent_title: list.parent_title || null,
          parent_type: list.parent_type || null,
          is_nested: list.is_nested || false,
          nesting_level: list.nesting_level || 0
        }));

        console.log(`[Basecamp API] Mapped ${mapped.length} todo lists:`, mapped);

        return {
          todoLists: mapped,
          timestamp: data.timestamp,
          account: data.account,
          project_id: data.project_id,
          message: data.message,
          _cached: Boolean(data._cached),
          _cached_at: data._cached_at || data._fetched_at || null,
          _stale: Boolean(data._stale),
        };
      }

      // If no todoLists or empty array, log and return empty structure
      console.warn(`[Basecamp API] No todoLists found in response for project ${projectId}`, {
        hasTodoLists: !!data.todoLists,
        message: data.message,
        dataKeys: Object.keys(data)
      });

      return {
        todoLists: [],
        timestamp: data.timestamp,
        account: data.account,
        project_id: data.project_id,
        message: data.message,
        _cached: Boolean(data._cached),
        _cached_at: data._cached_at || data._fetched_at || null,
        _stale: Boolean(data._stale),
      };
    } catch (error) {
      console.error(`[Basecamp API] Exception fetching todo lists for project ${projectId}:`, error);
      throw error;
    }
  },

  /**
   * Fetch a single todo by ID
   */
  getTodoById: async (projectId: string, todoId: string, forceRefresh: boolean = false): Promise<BasecampTodo> => {
    try {
      const params = forceRefresh ? '?refresh=true' : '';
      const response = await fetchWithAuth(`/api/todo/${projectId}/${todoId}${params}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 404) {
          // Expected/recoverable: this ID may belong to a todolist or BC5 step rather than a
          // todo - the caller (useBasecampTodoData) checks both before treating it as missing.
          // Logging at error level here would be misleading console noise for a handled case.
          throw new Error(`BASECAMP_TODO_NOT_FOUND:${todoId}`);
        }
        console.error(`[Basecamp API] Error fetching todo ${todoId}:`, response.status, errorData);
        if (response.status === 406) {
          throw new Error(
            `Basecamp refused live data for this todo (406). Open it in Basecamp or use the ingested work spec.`
          );
        }
        if (response.status === 503) {
          throw new Error(
            'Maater API is restarting or the database is briefly unavailable. Retry in a few seconds.'
          );
        }
        handleAuthError({ status: response.status, ...errorData });
      }
      const data = await response.json();

      // Return the todo from the response
      return data.todo || data;
    } catch (error) {
      const isNotFound = error instanceof Error && error.message.startsWith('BASECAMP_TODO_NOT_FOUND');
      if (!isNotFound) {
        console.error(`[Basecamp API] Exception fetching todo ${todoId}:`, error);
      }
      throw error;
    }
  },

  /**
   * Project people for Assigned to add/remove.
   */
  getProjectPeople: async (projectId: string): Promise<Array<{
    id: string;
    name: string;
    email?: string;
    client?: boolean;
    company?: string;
  }>> => {
    const response = await fetchBasecampApi(`/api/basecamp/projects/${projectId}/people`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      handleAuthError({ status: response.status, ...errorData });
    }
    const data = await response.json();
    return Array.isArray(data?.people) ? data.people : [];
  },

  /**
   * Fast, cache-first lookup of a Basecamp todo/todolist ID against the local database
   * (basecamp_todos cache, falling back to ingested FlowNotes documents). No live Basecamp
   * API call - used to disambiguate todo vs todolist IDs without the cost of a live fetch.
   */
  searchTodoRecord: async (
    todoId: string
  ): Promise<{
    found: boolean;
    todo?: { projectId?: string; recordType?: 'todo' | 'todolist'; appUrl?: string | null; title?: string };
  }> => {
    try {
      const response = await fetchWithAuth(`/api/basecamp/search-todo/${todoId}`, {
        credentials: 'include',
      });
      if (!response.ok) return { found: false };
      return await response.json();
    } catch {
      return { found: false };
    }
  },

  /**
   * Resolve a BC5 subtask to its parent todo id (for deep-link redirect)
   */
  getStepById: async (
    projectId: string,
    stepId: string,
  ): Promise<{ step: Record<string, unknown>; parentTodoId: string }> => {
    const response = await fetchWithAuth(`/api/basecamp/step/${projectId}/${stepId}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Step not found: ${stepId}`);
    }
    const data = await response.json();
    return {
      step: data.step,
      parentTodoId: String(data.parentTodoId),
    };
  },

  /**
   * Mark a BC5 subtask complete or incomplete
   */
  setStepCompletion: async (
    projectId: string,
    stepId: string,
    completed: boolean,
  ): Promise<void> => {
    const response = await fetchWithAuth(
      `/api/basecamp/step/${projectId}/${stepId}/completion`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to update subtask ${stepId}`);
    }
  },

  /**
   * Search for FlowNotes documents that reference a Basecamp todo ID (work specs).
   * Uses limit=50 by default so the Work Specs panel can show all docs in the todolist folder.
   */
  getTodoRelatedDocuments: async (todoId: string, limit: number = 50, organizationId?: string): Promise<any[]> => {
    try {
      const response = await fetchWithAuth(
        withOrganizationId(`/api/basecamp/todo-documents/${todoId}?limit=${limit}`, organizationId),
        {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`[Basecamp API] Error searching for todo documents:`, response.status, errorData);
        throw new Error(`Failed to search documents: ${response.statusText}`);
      }
      const data = await response.json();
      // Filter out low-rank matches (rank < 70) - these are just casual mentions, not actual links
      // Rank thresholds:
      // - 100: File path match (highest confidence)
      // - 90-95: Frontmatter match (high confidence)
      // - 70-85: Frontmatter variations (medium confidence)
      // - 50: Content mention (low confidence - filter these out)
      const documents = (data.documents || []).filter((doc: any) => doc.matchScore >= 70);
      console.log(`[Basecamp API] Filtered ${(data.documents || []).length - documents.length} low-confidence matches for todo ${todoId}`);
      return documents;
    } catch (error) {
      console.error(`[Basecamp API] Exception searching for todo documents:`, error);
      throw error;
    }
  },

  /**
   * Get semantic and casual connections for a Basecamp todo
   */
  getTodoConnections: async (
    todoId: string,
    limit: number = 10,
    similarityThreshold: number = 0.3,
    organizationId?: string
  ): Promise<{
    semantic: any[];
    casual: any[];
    todos: RelatedTodo[];
    total: number;
  }> => {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        similarityThreshold: similarityThreshold.toString()
      });
      if (organizationId) params.set('organizationId', organizationId);
      const response = await fetchWithAuth(`/api/basecamp/todo-connections/${todoId}?${params.toString()}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        if (response.status === 503 || response.status >= 502) {
          return { semantic: [], casual: [], todos: [], total: 0 };
        }
        const errorData = await response.json().catch(() => ({}));
        console.error(`[Basecamp API] Error fetching connections:`, response.status, errorData);
        throw new Error(`Failed to fetch connections: ${response.statusText}`);
      }
      return response.json();
    } catch (error) {
      if (isOfflineOrUnreachableFetchError(error)) {
        return { semantic: [], casual: [], todos: [], total: 0 };
      }
      console.error(`[Basecamp API] Exception fetching connections:`, error);
      throw error;
    }
  },

  /**
   * Generate Basecamp notes HTML from FlowNotes document
   */
  getBasecampNotes: async (todoId: string, organizationId?: string): Promise<{
    html: string;
    documentPath?: string;
    documentTitle?: string;
  }> => {
    try {
      const response = await fetchWithAuth(withOrganizationId(`/api/basecamp/todo/${todoId}/basecamp-notes`, organizationId), {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      return response.json();
    } catch (error) {
      console.error(`[Basecamp API] Error generating Basecamp notes:`, error);
      throw error;
    }
  },

  /**
   * Generate Basecamp notes markdown file from FlowNotes document
   * Supports SSE streaming with thinking mode events
   */
  generateBasecampNotesMarkdown: async (
    todoId: string,
    onProgress?: (event: any) => void,
    organizationId?: string
  ): Promise<{
    success: boolean;
    html: string;
    title: string;
  }> => {
    try {
      const response = await fetchWithAuth(
        withOrganizationId(`/api/basecamp/todo/${todoId}/basecamp-notes/markdown?stream=true`, organizationId),
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }

      // If streaming, handle SSE events
      if (response.headers.get('content-type')?.includes('text/event-stream')) {
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let result: any = null;

        if (!reader) {
          throw new Error('Response body is not readable');
        }

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmed = line.replace(/\r$/, '');
            if (!trimmed.startsWith('data: ')) continue;

            const data = trimmed.slice(6).trim();
            if (data === '[DONE]') {
              if (!result) {
                throw new Error('Stream ended without result');
              }
              return result;
            }

            let event: Record<string, unknown>;
            try {
              event = JSON.parse(data) as Record<string, unknown>;
            } catch {
              console.warn('[Basecamp API] Failed to parse SSE JSON:', data);
              continue;
            }

            if (onProgress) {
              onProgress(event);
            }

            const type = event.type as string | undefined;
            if (type === 'thinking_complete' && event.result) {
              result = event.result;
            } else if (type === 'error') {
              throw new Error(
                (typeof event.error === 'string' && event.error) ||
                  (typeof event.message === 'string' && event.message) ||
                  'Unknown error'
              );
            }
          }
        }

        if (!result) {
          throw new Error('Stream ended without result');
        }
        return result;
      } else {
        // Fallback to regular JSON response
        return response.json();
      }
    } catch (error) {
      console.error(`[Basecamp API] Error generating Basecamp notes markdown:`, error);
      throw error;
    }
  },

  /**
   * Generate Basecamp notes HTML from markdown content
   */
  generateBasecampNotesHTML: async (todoId: string, markdown: string, frontmatter?: Record<string, any>, organizationId?: string): Promise<{
    html: string;
    wordCount: number;
  }> => {
    try {
      const response = await fetchWithAuth(withOrganizationId(`/api/basecamp/todo/${todoId}/basecamp-notes/html`, organizationId), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ markdown, frontmatter }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      return response.json();
    } catch (error) {
      console.error(`[Basecamp API] Error generating Basecamp notes HTML:`, error);
      throw error;
    }
  },

  /**
   * Create a Basecamp todo on a todolist (approval-gated; this helper always sends confirmed: true).
   */
  createTodo: async (payload: {
    todolistUrl: string
    content: string
    description?: string
    due_on?: string
  }): Promise<{
    success: boolean
    dryRun?: boolean
    todoUrl?: string
    todo?: { id: number; title: string; url: string; due_on?: string | null }
    message?: string
    error?: string
  }> => {
    try {
      const response = await fetchWithAuth('/ai/todo/create', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          todolistUrl: payload.todolistUrl,
          content: payload.content,
          description: payload.description,
          due_on: payload.due_on,
          options: { confirmed: true },
        }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        if (response.status === 401 || response.status === 503) {
          handleAuthError({ status: response.status, ...data })
        }
        throw new Error(data.error || data.message || 'Failed to create todo')
      }
      return data
    } catch (error) {
      console.error('[Basecamp API] Error creating todo:', error)
      throw error
    }
  },

  /**
   * Push the approved Basecamp notes HTML to the todo description.
   * Cloud-first: html is passed directly - nothing read from disk or DB.
   */
  pushNotesToBasecamp: async (todoId: string, todoUrl: string, html: string, organizationId?: string): Promise<{
    success: boolean;
    todo: { id: number; title: string; url: string };
  }> => {
    try {
      const response = await fetchWithAuth(
        withOrganizationId(`/api/basecamp/todo/${todoId}/basecamp-notes/push`, organizationId),
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ todoUrl, html }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      return response.json();
    } catch (error) {
      console.error(`[Basecamp API] Error pushing notes to Basecamp:`, error);
      throw error;
    }
  },

  /**
   * Fetch comments for a specific todo (GET /api/comments — same auth as other /api Basecamp routes).
   * @param todoUrl - Full Basecamp todo URL, or pass projectId + todoId as second arg
   */
  getComments: async (
    todoUrl: string,
    todoId?: string,
  ): Promise<BasecampComment[]> => {
    try {
      let projectId: string;
      let resolvedTodoId: string;
      if (todoId != null) {
        projectId = todoUrl;
        resolvedTodoId = todoId;
      } else {
        const parsed = parseBasecampTodoIdsFromUrl(todoUrl);
        if (!parsed) {
          throw new Error('Invalid Basecamp todo URL (expected .../buckets/{projectId}/todos/{todoId})');
        }
        projectId = parsed.projectId;
        resolvedTodoId = parsed.todoId;
      }
      const response = await fetchBasecampApi(`/api/comments/${projectId}/${resolvedTodoId}`, {
        credentials: 'include',
      });
      const data = await response.json();
      return Array.isArray(data) ? data : data.comments || data;
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      throw error;
    }
  },

  /**
   * Post a plain-text comment on a Basecamp todo
   */
  postTodoComment: async (
    projectId: string,
    todoId: string,
    content: string,
  ): Promise<{ success: boolean; comment?: Record<string, unknown> }> => {
    const response = await fetchWithAuth(`/api/comments/${projectId}/${todoId}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content }),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to post comment');
    }
    return response.json();
  },

  /**
   * Fetch project activity (last N days)
   * @param since - Optional ISO date string to fetch only activity since this date (incremental fetch)
   */
  getProjectActivity: async (
    projectId: string,
    days: number = 7,
    forceRefresh: boolean = false,
    since?: string
  ): Promise<{
    activities: BasecampActivity[];
    _cached?: boolean;
    _cached_at?: string;
    _stale?: boolean;
  }> => {
    try {
      const params = new URLSearchParams({ days: days.toString() });
      if (forceRefresh) params.set('refresh', 'true');
      if (since) params.set('since', since);

      const response = await fetchWithAuth(`/api/project-activity/${projectId}?${params.toString()}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      const data = await response.json();

      // Backend returns { events_by_date: { "2025-10-22": [...], ... } }
      // Each event has a nested recording object with title/type
      // Transform into flat BasecampActivity structure
      const allActivities: BasecampActivity[] = [];
      if (data.events_by_date && typeof data.events_by_date === 'object') {
        for (const dayEvents of Object.values(data.events_by_date)) {
          if (Array.isArray(dayEvents)) {
            for (const event of dayEvents) {
              // Transform event + recording into flat activity
              const activity: BasecampActivity = {
                id: event.id?.toString() || '',
                type: (event.recording?.type?.toLowerCase() as 'todo' | 'message' | 'document') || 'todo',
                title: event.recording?.title || event.summary || 'Activity',
                url: event.url || '',
                created_at: event.created_at,
                creator: {
                  name: event.creator?.name || 'Unknown',
                },
                ...(event.recording?.id && { recording_id: event.recording.id.toString() }),
              };

              allActivities.push(activity);
            }
          }
        }
        // Sort by created_at desc (most recent first)
        allActivities.sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      }

      return {
        activities: allActivities,
        _cached: data._cached || false,
        _cached_at: data._cached_at,
        _stale: data._stale || false,
      };
    } catch (error) {
      console.error('Failed to fetch project activity:', error);
      throw error;
    }
  },

  /**
   * Get assigned todos for current user (Reports API - single fast call).
   * Returns same shape as getMyAssignmentsDetailed for use in Todos/My Stuff/Triage.
   */
  getAssignments: async (forceRefresh = false, organizationId?: string): Promise<{
    assignments: BasecampAssignment[];
    _cached?: boolean;
    _cached_at?: string;
    _stale?: boolean;
  }> => {
    try {
      const base = forceRefresh ? `/api/assignments?refresh=true` : `/api/assignments`;
      const url = withOrganizationId(base, organizationId);
      const response = await fetchWithAuth(url, { credentials: 'include' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      const data = await response.json();
      const rawList: any[] = Array.isArray(data)
        ? (data[0]?.todos != null ? data.flatMap((item: any) => item.todos || []) : data)
        : data?.assignments ?? data?.todos ?? [];
      const today = todayInLosAngeles();
      const assignments: BasecampAssignment[] = rawList
        .filter((t: any) => t && !t.completed)
        .map((t: any) => {
          const bucket = t.bucket ?? t.project;
          const projectId = bucket?.id ?? t.bucket_id;
          const projectName = bucket?.name ?? t.project?.name ?? '';
          const dueOn = t.due_on || undefined;
          const acct =
            String(t.app_url || t.url || '').match(
              /\/\/(?:3|app)\.basecamp(?:api)?\.com\/(\d+)\//
            )?.[1] ?? null;
          const appUrl =
            t.app_url ??
            (acct && projectId && t.id
              ? buildBasecampWebUrl(t.id, projectId, 'todo', acct)
              : '');
          const recording = normalizeAssignmentRecordingFields(t);
          const assigneesRaw = Array.isArray(t.assignees)
            ? t.assignees
            : t.assignee
              ? [t.assignee]
              : [];
          const assignees = assigneesRaw
            .filter((p: any) => p && (p.id != null || p.name))
            .map((p: any) => ({
              id: String(p.id ?? ''),
              name: String(p.name ?? '').trim(),
              email:
                typeof p.email_address === 'string'
                  ? p.email_address
                  : typeof p.email === 'string'
                    ? p.email
                    : undefined,
              client: typeof p.client === 'boolean' ? p.client : undefined,
            }))
            .filter((p: { name: string }) => p.name);
          const primary = assignees[0] ?? t.assignee ?? { id: '', name: '' };
          return {
            id: String(t.id),
            title: t.content ?? t.title ?? '',
            url: appUrl,
            due_on: dueOn,
            completed: !!t.completed,
            project: { id: String(projectId || ''), name: projectName },
            assignee: {
              id: String(primary.id ?? ''),
              name: String(primary.name ?? ''),
            },
            assignees,
            _project_name: projectName,
            _is_overdue: !!(dueOn && dueOn < today),
            _parent_title: recording._parent_title ?? t.parent?.title,
            ...(t.parent?.id != null
              ? { _todolist_id: String(t.parent.id) }
              : t._todolist_id
                ? { _todolist_id: String(t._todolist_id) }
                : {}),
            _recording_type: recording._recording_type,
            _parent_todo_id: recording._parent_todo_id,
            _app_url: appUrl,
            _updated_at: t.updated_at,
            _workspec_status: t._workspec_status ?? null,
            _workspec_file_path: t._workspec_file_path ?? null,
            _workspec_enriched_at: t._workspec_enriched_at ?? null,
            _last_comment_text: t._last_comment_text ?? null,
            _last_comment_at: t._last_comment_at ?? null,
            _last_comment_creator: t._last_comment_creator ?? null,
            _last_comment_cached_at: t._last_comment_cached_at ?? null,
          } as BasecampAssignment;
        });
      return {
        assignments,
        _cached: false,
        _cached_at: new Date().toISOString(),
        _stale: false,
      };
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
      throw error;
    }
  },

  /**
   * Org mode: permission-gated shared basecamp_todos cache (not Reports assigned-to-me).
   */
  getOrgAssignments: async (
    organizationId?: string,
    opts?: {
      statusFilter?: string;
      assigneeFacet?: string;
      assigneePerson?: string | null;
      refreshPermissions?: boolean;
    },
  ): Promise<{
    assignments: BasecampAssignment[];
    _cached?: boolean;
    _cached_at?: string;
    _source?: string;
    _allowed_project_count?: number;
    _cached_row_count?: number;
    _empty_reason?: string | null;
    _me?: { id?: string | null; email?: string | null; name?: string | null } | null;
  }> => {
    const params = new URLSearchParams();
    if (opts?.statusFilter) params.set('statusFilter', opts.statusFilter);
    if (opts?.assigneeFacet) params.set('assigneeFacet', opts.assigneeFacet);
    if (opts?.assigneePerson) params.set('assigneePerson', opts.assigneePerson);
    if (opts?.refreshPermissions) params.set('refreshPermissions', 'true');
    const qs = params.toString();
    const base = `/api/assignments/org${qs ? `?${qs}` : ''}`;
    const url = withOrganizationId(base, organizationId);
    const response = await fetchWithAuth(url, { credentials: 'include' });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      handleAuthError({ status: response.status, ...errorData });
    }
    const data = await response.json();
    const rawList: any[] = Array.isArray(data?.assignments) ? data.assignments : [];
    return {
      assignments: rawList as BasecampAssignment[],
      _cached: true,
      _cached_at: data._cached_at || new Date().toISOString(),
      _source: data._source || 'org_cache',
      _allowed_project_count: data._allowed_project_count,
      _cached_row_count:
        typeof data._cached_row_count === 'number' ? data._cached_row_count : rawList.length,
      _empty_reason: data._empty_reason || null,
      _me: data._me && typeof data._me === 'object' ? data._me : null,
    };
  },

  /**
   * Get assigned todos for current user (legacy - raw array)
   */
  getMyAssignments: async (organizationId?: string): Promise<BasecampAssignment[]> => {
    try {
      const response = await fetchWithAuth(withOrganizationId(`/api/assignments`, organizationId), {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      return response.json();
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
      throw error;
    }
  },

  /**
   * Get detailed assignments for current user (with caching support)
   */
  getMyAssignmentsDetailed: async (
    forceRefresh = false,
    projectLimit?: number,
    bookmarkedOnly = false,
    projectId?: string | null,
    signal?: AbortSignal,
    organizationId?: string
  ): Promise<{
    assignments: BasecampAssignment[];
    _cached?: boolean;
    _cached_at?: string;
    _stale?: boolean;
    _fetched_at?: string;
    _bookmarked_only?: boolean;
  }> => {
    try {
      const params = new URLSearchParams();
      if (forceRefresh) params.set('refresh', 'true');
      if (projectLimit) params.set('limit', projectLimit.toString());
      // Use 'monitored' parameter (renamed from 'bookmarked')
      if (bookmarkedOnly) params.set('monitored', 'true');
      if (projectId) params.set('project_id', projectId);
      if (organizationId) params.set('organizationId', organizationId);

      const url = `/api/my-assignments-detailed${params.toString() ? '?' + params.toString() : ''}`;

      console.log('[Basecamp API] Fetching assignments:', { url, forceRefresh, projectLimit, projectId, monitoredOnly: bookmarkedOnly, organizationId });

      const response = await fetchWithAuth(url, {
        credentials: 'include',
        signal,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      const data = await response.json();

      // Handle both array response (legacy) and object response (new with cache metadata)
      if (Array.isArray(data)) {
        return {
          assignments: data,
          _cached: false,
        };
      }

      return data;
    } catch (error) {
      console.error('Failed to fetch detailed assignments:', error);
      throw error;
    }
  },

  /**
   * Get comments for a specific project/todo combination
   */
  getProjectTodoComments: async (projectId: string, todoId: string): Promise<BasecampComment[]> => {
    try {
      const response = await fetchWithAuth(`/api/comments/${projectId}/${todoId}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      return response.json();
    } catch (error) {
      console.error('Failed to fetch project todo comments:', error);
      throw error;
    }
  },

  /**
   * Get todo details by URL
   */
  getTodoByUrl: async (todoUrl: string): Promise<any> => {
    try {
      const response = await fetchWithAuth(`/ai/todo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ todoUrl }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      const data = await response.json();
      return data.todo || data; // Handle wrapped response
    } catch (error) {
      console.error('Failed to fetch todo details:', error);
      throw error;
    }
  },

  /**
   * Get linked FlowNotes documents for a Basecamp project
   */
  getLinkedDocuments: async (projectId: string, limit: number = 20): Promise<any> => {
    try {
      const response = await fetchWithAuth(`/api/project-link/${projectId}/documents?limit=${limit}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch linked documents:', error);
      throw error;
    }
  },

  /**
   * Set FlowNotes project link
   */
  setProjectLink: async (projectId: string, projectPath: string, projectTag?: string): Promise<any> => {
    try {
      const response = await fetchWithAuth(`/api/project-link/${projectId}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          flownotes_project_path: projectPath,
          flownotes_project_tag: projectTag,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to set project link:', error);
      throw error;
    }
  },

  /**
   * Get user's project monitoring preferences
   */
  getProjectPreferences: async (organizationId?: string): Promise<Record<string, { isMonitored: boolean; isPinned: boolean; customName?: string }>> => {
    try {
      const response = await fetchWithAuth(withOrganizationId(`/api/basecamp/project-preferences`, organizationId), {
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      const data = await response.json();
      return data.preferences || {};
    } catch (error) {
      console.error('Failed to fetch project preferences:', error);
      throw error;
    }
  },

  /**
   * Update project monitoring preference
   */
  updateProjectPreference: async (
    projectId: string,
    isMonitored?: boolean,
    isPinned?: boolean,
    customName?: string,
    organizationId?: string
  ): Promise<void> => {
    try {
      const response = await fetchWithAuth(withOrganizationId(`/api/basecamp/project-preferences`, organizationId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ projectId, isMonitored, isPinned, customName }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
    } catch (error) {
      console.error('Failed to update project preference:', error);
      throw error;
    }
  },

  /**
   * Get todos with work spec status for a project
   */
  getTodosWithWorkSpecs: async (projectId: string, limit: number = 100, organizationId?: string): Promise<{
    projectId: string;
    count: number;
    todos: Array<{
      todoId: string;
      title: string;
      completed: boolean;
      dueOn: string | null;
      hasWorkSpec: boolean;
      workSpecId: string | null;
      workSpecTitle: string | null;
      workSpecPath: string | null;
    }>;
  }> => {
    try {
      const response = await fetchWithAuth(
        withOrganizationId(`/api/basecamp/projects/${projectId}/todos-with-work-specs?limit=${limit}`, organizationId),
        {
        credentials: 'include',
      });
      if (!response.ok) {
        // If 404, endpoint might not be available yet (server needs restart)
        // Return empty result instead of throwing error
        if (response.status === 404) {
          console.warn('[Basecamp API] todos-with-work-specs endpoint not found (404). Server may need restart.');
          return {
            projectId,
            count: 0,
            todos: []
          };
        }
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      return await response.json();
    } catch (error) {
      // Don't throw - gracefully degrade to empty result
      console.warn('[Basecamp API] Failed to fetch todos with work specs:', error);
      return {
        projectId,
        count: 0,
        todos: []
      };
    }
  },

  /**
   * Get user's bookmarked items
   * @param forceRefresh - If true, re-syncs: Basecamp has no list endpoint (204), so FlowNotes
   *   verifies each locally stored bookmark via bookmark_url and refreshes todo cache.
   */
  getMyBookmarks: async (forceRefresh = false, organizationId?: string): Promise<{
    items: BasecampAssignment[];
    cached_at?: string;
    stale?: boolean;
  }> => {
    try {
      const params = new URLSearchParams();
      if (forceRefresh) params.set('refresh', 'true');
      if (organizationId) params.set('organizationId', organizationId);
      const url = `/api/basecamp/my/bookmarks${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetchWithAuth(url, { credentials: 'include' });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      const data = await response.json();
      return {
        items: data.items || [],
        cached_at: data.cached_at,
        stale: data.stale || false,
      };
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
      throw error;
    }
  },

  /**
   * Check bookmark status for a todo
   */
  getBookmarkStatus: async (
    projectId: string,
    todoId: string,
    organizationId?: string
  ): Promise<{
    bookmarked: boolean;
    bookmark_url: string | null;
  }> => {
    try {
      const response = await fetchWithAuth(
        withOrganizationId(`/api/basecamp/bookmarks/${projectId}/${todoId}/status`, organizationId),
        {
        credentials: 'include',
      });

      // Handle non-200 responses (should be rare now, but handle gracefully)
      if (!response.ok) {
        // If 404, return not bookmarked (non-critical)
        if (response.status === 404) {
          return { bookmarked: false, bookmark_url: null };
        }
        const errorData = await response.json().catch(() => ({}));
        // Bookmark status is non-critical. Do not toast Reconnect here -
        // document-viewer and the todo hook both call this, which doubled
        // 401s. Primary todo/projects fetches own the reauth CTA.
        if (import.meta.env.DEV) {
          console.debug('[Basecamp API] Bookmark status check failed (non-critical):', response.status, errorData);
        }
        return { bookmarked: false, bookmark_url: null };
      }

      return await response.json();
    } catch (error) {
      // Non-critical error - return not bookmarked instead of throwing
      if (import.meta.env.DEV) {
        console.debug('[Basecamp API] Failed to check bookmark status (non-critical):', error);
      }
      return { bookmarked: false, bookmark_url: null };
    }
  },

  /**
   * Add todo to bookmarks
   */
  addBookmark: async (
    projectId: string,
    todoId: string,
    organizationId?: string
  ): Promise<{
    success: boolean;
    bookmarked: boolean;
    bookmark_url: string;
    synced_with_basecamp: boolean;
  }> => {
    try {
      const response = await fetchWithAuth(withOrganizationId(`/api/basecamp/bookmarks/${projectId}/${todoId}`, organizationId), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to add bookmark:', error);
      throw error;
    }
  },

  /**
   * Remove todo from bookmarks
   */
  removeBookmark: async (
    projectId: string,
    todoId: string,
    organizationId?: string
  ): Promise<{
    success: boolean;
    bookmarked: boolean;
    synced_with_basecamp: boolean;
  }> => {
    try {
      const response = await fetchWithAuth(withOrganizationId(`/api/basecamp/bookmarks/${projectId}/${todoId}`, organizationId), {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        handleAuthError({ status: response.status, ...errorData });
      }
      return await response.json();
    } catch (error) {
      console.error('Failed to remove bookmark:', error);
      throw error;
    }
  },

  /** Distinct note-field tags harvested from org-cached Basecamp todo YAML. */
  getNoteTags: async (organizationId?: string): Promise<string[]> => {
    const response = await fetchBasecampApi(
      withOrganizationId('/api/basecamp/note-tags', organizationId),
      { credentials: 'include' },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to load note tags');
    }
    const data = await response.json();
    return Array.isArray(data.tags) ? data.tags : [];
  },

  suggestNoteTags: async (opts: {
    title?: string;
    body?: string;
    current?: string[];
    catalog?: string[];
    avoid?: string[];
    organizationId?: string;
  }): Promise<string[]> => {
    const response = await fetchBasecampApi(
      withOrganizationId('/api/basecamp/suggest-note-tags', opts.organizationId),
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: opts.title || '',
          body: opts.body || '',
          current: opts.current || [],
          catalog: opts.catalog || [],
          avoid: opts.avoid || [],
        }),
      },
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to suggest tags');
    }
    const data = await response.json();
    return Array.isArray(data.tags) ? data.tags : [];
  },
};
